// Typed calls for /api/field (backend src/wcc_viewer/routers/field.py).
import { getJson, postJson } from "../api";

export interface Option {
  key: string | number;
  label: string;
}
export interface FormField {
  key: string;
  label: string;
  type: "text" | "number" | "select" | "bool" | "textarea";
  options?: string[];
  /** key -> label for a select's options (row #49); a key without one shows as itself */
  option_labels?: Record<string, string>;
  /** "Other (specify)": the value "other" plus the typed text in <key>_other */
  other?: boolean;
}
export interface FieldConfig {
  asset_types: Option[];
  fault_types: (Option & { fault_class: string })[];
  failure_components: Option[];
  default_component: Record<string, string>;
  severities: Option[];
  forms: Record<string, { label: string; workspace: string; fields: FormField[] }>;
  limits: { max_photo_bytes: number; max_photos_per_capture: number; warn_accuracy_m: number };
  safety_banner: string;
  is_reviewer: boolean;
  profile: "public" | "full";
  redaction_implemented: boolean;
  fault_history_available: boolean;
}

export interface Candidate {
  asset_id: string;
  asset_type: string;
  kind: "point" | "line";
  label: string;
  lon?: number;
  lat?: number;
  path?: [number, number][];
  road_name?: string | null;
  network?: string | null;
  water_type?: string | null;
  material?: string | null;
  diameter_mm?: number | null;
  type_label?: string | null;
  address?: string | null;
  source?: string;
  attribution?: string;
  /** valves: normal status (open / closed / unknown) */
  normal_status?: string | null;
  distance_m: number;
  bearing_deg: number | null;
  compass: string | null;
  relative_bearing_deg: number | null;
  along_road: boolean;
  score: number;
  rank: number;
  reasons: string[];
  /** /nearby with a project_id: one of the project's own assets / on its network (ranked first) */
  in_project?: boolean;
  project_network?: boolean;
}

export interface RoadContext {
  road: { name: string; distance_m: number; bearing_axis_deg: number } | null;
  cross_street: { name: string; distance_m: number; kind: string } | null;
  facing: { heading_deg: number; compass: string; relation_to_road: string; road_ahead: { name: string; distance_m: number } | null } | null;
  side: string | null;
  confidence: string;
  sentence: string | null;
  source: string;
}

export interface NearbyResult {
  candidates: Candidate[];
  road: RoadContext;
  search_radius_m: number;
  n_found: number;
  profile: string;
  note: string;
}

export interface LiveJob {
  request_type: string;
  status: string;
  water: string;
  priority: string;
  reported: string | null;
  address: string | null;
  lon: number;
  lat: number;
  distance_m: number;
  bearing_deg: number;
  compass: string;
}

export interface CapturePhoto {
  id: number;
  seq: number;
  /** blurred shared copies (thumb + shared) are ready (F5) */
  processed: boolean;
  blurred?: boolean;
  redaction?: { status: string | null; rev: number; n_auto: number; n_manual: number };
  width: number | null;
  height: number | null;
  exif_had_gps: boolean;
  error: string | null;
}

/** A blur region, normalised to the image (x, y, w, h in 0..1). */
export interface BlurRegion {
  id?: string;
  kind: string;
  box: [number, number, number, number];
  score?: number;
  source?: string;
}
export interface BlurRegions {
  photo_id: number;
  status: string | null;
  rev: number;
  auto: BlurRegion[];
  manual: BlurRegion[];
  suppressed: string[];
  error: string | null;
}

async function putJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { detail?: unknown };
      if (typeof j.detail === "string") msg = j.detail;
    } catch {
      /* not JSON */
    }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface Capture {
  id: number;
  client_id: number;
  user_id: number;
  user_name: string | null;
  team: string | null;
  form_kind: string;
  captured_at: string | null;
  received_at: string;
  lat: number | null;
  lon: number | null;
  accuracy_m: number | null;
  heading_deg: number | null;
  asset_id: string | null;
  asset_type: string | null;
  fault_type: string | null;
  failure_component: string | null;
  severity: number | null;
  status: string;
  notes: string | null;
  form: Record<string, unknown> | null;
  road_context: RoadContext | null;
  asset_candidates: { asset_id: string; distance_m: number }[] | null;
  project_id: string | null;
  processing_status: string;
  review_note: string | null;
  reviewed_by_name: string | null;
  confirmed_component: string | null;
  asset_issue_id: number | null;
  photos: CapturePhoto[];
  can_see_original: boolean;
  duplicate?: boolean;
}

export interface FieldProject {
  id: string;
  name: string;
  status: string;
  phase: string | null;
  phase_label: string | null;
  n_assets: number;
  n_captures: number;
  in_construction: boolean;
  mine: boolean;
  role: string | null;
}

export interface AssetHistory {
  asset_id: string;
  maximo_events: Record<string, string | number | null>[];
  break_model_events: Record<string, string | number | null>[];
  break_model: Record<string, string | number | null> | null;
  n_maximo: number;
  n_break_model: number;
}

// F7 AR overlay (backend routers/field_ar_packs.py, field_ar.py)
export interface ArAsset {
  asset_id: string;
  asset_type: string;
  kind: "point" | "line";
  label: string;
  colour: string;
  network: string;
  network_label: string;
  type_label: string;
  material: string | null;
  diameter_mm: number | null;
  source?: string;
  attribution?: string;
  address?: string | null;
  depth_label: string | null;
  e?: number;
  n?: number;
  path_enu?: [number, number][];
  distance_m: number;
  uncertainty_m: number;
}
export interface ArCalCandidate {
  asset_id: string;
  asset_type: string;
  type_label: string;
  label: string | null;
  network: string;
  colour: string | null;
  address?: string | null;
  e: number;
  n: number;
  distance_m: number;
  bearing_deg: number;
  angle_off_deg: number;
  range_off_m: number | null;
  score: number;
  /** metres from where the tap met the ground (null: no usable range) */
  tap_point_dist_m?: number | null;
  /** other candidates within field_ar.CAL_CLOSE_PAIR_M of this one */
  close_to?: string[];
}
export interface ArCalCandidates {
  candidates: ArCalCandidate[];
  auto_pick: string | null;
  ambiguous?: boolean;
  kind: string | null;
  kinds: Record<string, string[]>;
  note: string | null;
  origin: { lat: number; lon: number };
}
export interface ArHeadingFix {
  accepted: boolean;
  heading_offset_deg: number;
  east_m: number;
  north_m: number;
  range_shift_m: number;
  method: "road" | "camera" | "kerb" | "road-camera";
  confidence: number;
  reason?: string;
  warning?: string;
  road: { name: string; key?: string; distance_m: number; direction_deg: number; bend_deg?: number; lane_count?: number | null; half_width_m?: number } | null;
  /** 2026-10-05: the other roads within reach ("Not this road?"); send one's key back as road_key */
  alternatives?: ArRoadAlt[];
  /** where the kerbs should be (LINZ centreline +/- half-width), ENU metres from the request's lat/lon: the preview draws them */
  kerb_lines_enu?: [number, number][][];
  // kerb only (2026-10-05): the shift is across the road, along `normal`
  normal?: [number, number] | null;
  lateral_shift_m?: number;
  position_sigma_m?: number;
  kerb?: { distance_m: number; length_m: number } | null;
  ttl_s: number;
  max_move_m: number;
  // camera only
  experimental?: boolean;
  uncertainty_deg?: number;
  n_lines?: number;
  lines?: { supporting_m: number; n_supporting: number; in_window_m: number; competitor_ratio: number; lateral_offsets_m: number[] };
  lateral_diagnostic?: { centreline_distance_m: number; expected_kerb_distances_m: number[]; observed_edge_offsets_m: number[]; applied: boolean; note: string };
}
/** Another road the calibration could align to (field_ar.road_alternatives). */
export interface ArRoadAlt {
  key: string;
  name: string;
  named: boolean;
  distance_m: number;
  direction_deg: number;
  off_axis_deg: number | null;
}
/** A line the camera-assisted road calibration found (field_ar_roadlines). */
export type ArRoadRole = "near_kerb" | "far_kerb" | "centre_line" | "edge";
export interface ArRoadLine {
  id: number;
  role: ArRoadRole;
  /** sideways offset from the camera, metres, + = right of the street direction */
  lateral_m: number;
  length_m: number;
  forward_m: [number, number];
  vehicle_frac: number;
  score: number;
  /** two ground points, ENU metres from the request point, in the RAW pose (drawn over the live camera) */
  ground_enu_raw: [number, number][];
  /** the shift across the road this line implies in each role (metres along `normal`) */
  implied_shift_m: { near_kerb: number; far_kerb: number; centre_line: number };
  position_sigma_m: number;
}
export interface ArRoadLines extends ArHeadingFix {
  candidates: ArRoadLine[];
  best: number | null;
  direction_confidence?: number;
  /** where the LINZ centreline is, sideways from the request point (+ = right of the street direction) */
  centreline_lateral_m?: number;
  vehicle_masked_share?: number;
  stored: boolean;
  /** the roads tried, best first (the chosen one, then other named roads) */
  tried?: { name: string; key: string; accepted: boolean; reason?: string | null }[];
}

export interface ArAssets {
  origin: { lat: number; lon: number };
  radius_m: number;
  assets: ArAsset[];
  n: number;
  uncertainty: { gps_m: number | null; compass_deg: number; min_compass_deg: number };
  camera_height_m: number;
  draw?: { max_range_m: number; fade_start_m: number; min_depression_deg: number; band_max_m: number; cal_residual_gps_m: number };
  profile: string;
  calibration: { ttl_s: number; max_move_m: number };
  warning: string;
}
export interface ArCalibration {
  heading_offset_deg: number;
  east_m: number;
  north_m: number;
  range_shift_m: number;
  accepted: boolean;
  reason?: string;
  asset_id: string;
  ttl_s: number;
  max_move_m: number;
  /** 2026-10-05: "position" (default from the phone) moves the position only; "full" also turns the heading */
  mode?: "position" | "full";
  clamped?: boolean;
}

/** POST /ar-calibrate mode drag | rotate | two_point (field_ar.direct_calibration) */
export interface ArDirectCalibration {
  mode: "drag" | "rotate" | "two_point";
  accepted: boolean;
  reason?: string;
  /** add to the heading offset in force */
  heading_offset_deg: number;
  /** move the device position by this */
  east_m: number;
  north_m: number;
  range_shift_m: number;
  residual_m: number | null;
  span_m: number | null;
  sanity?: { level: "ok" | "warn" | "refuse"; move_m: number; heading_deg: number; limit_m: number; message: string | null; residual_m?: number | null };
  ttl_s: number;
  max_move_m: number;
  safety: string;
}

// F8 offline basemap packs (field_packs.py)
export interface TileRange {
  z: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  n: number;
}
export interface PackManifest {
  name: string;
  bbox: [number, number, number, number];
  zmin: number;
  zmax: number;
  layers: string[];
  ranges: TileRange[];
  url_template: string;
  tiles: number;
  est_bytes: number;
  est_mb: number;
  area_km2: number;
  caps: { max_tiles: number; max_mb: number; max_area_km2: number; zoom: [number, number] };
  ok: boolean;
  reasons: string[];
  max_zoom_that_fits: number | null;
  attribution: string;
  created_at: string;
  expires_at: string;
  expiry_days: number;
  fetch: { header: string; concurrency: number };
  project_id: string | null;
}
export type PackArea =
  | { kind: "point"; lat: number; lon: number; radius_m: number }
  | { kind: "box"; west: number; south: number; east: number; north: number }
  | { kind: "project"; project_id: string };

function areaParams(a: PackArea): Record<string, string | number> {
  if (a.kind === "point") return { lat: a.lat, lon: a.lon, radius_m: a.radius_m };
  if (a.kind === "box") return { west: a.west, south: a.south, east: a.east, north: a.north };
  return { project_id: a.project_id };
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== "") u.set(k, String(v));
  return u.toString();
}

// The field map's faint "Area" layer (GET /api/field/area, field_assets.area_assets).
// Compact: a line has `path`, a point lon/lat; null fields are left out.
export interface AreaAsset {
  asset_id: string;
  asset_type: string;
  label: string;
  network?: string | null;
  material?: string | null;
  diameter_mm?: number | null;
  type_label?: string | null;
  address?: string | null;
  source?: string;
  test_region?: string;
  /** valves: normal status (open / closed / unknown) */
  normal_status?: string;
  lon?: number;
  lat?: number;
  path?: [number, number][];
}
export interface AreaResult {
  center: [number, number];
  radius_m: number;
  max_radius_m: number;
  n: number;
  assets: AreaAsset[];
  roads: { name: string; path: [number, number][] }[];
  profile: string;
  road_source: string;
  /** GIS-view organisations: what the facility toggles show (its council's open data, or WWL's public layers) */
  area_note?: string;
  test_regions?: { key: string; attribution: string }[];
  /** tiled loading (areaTiles.ts): tiles drawn so far / needed for the radius */
  loaded_tiles?: number;
  total_tiles?: number;
}

/** A field request that got no answer in time (the server is busy, or the
 * phone is switching between Wi-Fi and mobile data). */
export class FieldTimeoutError extends Error {
  constructor(ms: number) {
    super(`no answer in ${Math.round(ms / 1000)} s`);
    this.name = "FieldTimeoutError";
  }
}

export const FIELD_TIMEOUT_MS = 8000;

async function getJsonTimeout<T>(url: string, ms = FIELD_TIMEOUT_MS): Promise<T> {
  const ac = new AbortController();
  const timer = window.setTimeout(() => ac.abort(), ms);
  try {
    return await getJson<T>(url, { signal: ac.signal });
  } catch (e) {
    if (ac.signal.aborted) throw new FieldTimeoutError(ms);
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

/** A request we cancelled ourselves (the crew walked on): not a failure. */
export class FieldCancelledError extends Error {
  constructor() {
    super("cancelled");
    this.name = "FieldCancelledError";
  }
}

/** /api/field/area-tiles (field_assets.area_tiles): the Area layer by z19 tile. */
export interface AreaTilesResult {
  z: number;
  tiles: Record<string, { assets: AreaAsset[]; roads: { name: string; path: [number, number][] }[] }>;
  n: number;
  profile: string;
  road_source: string;
  area_note?: string;
  test_regions?: { key: string; attribution: string }[];
}

export interface Timed<T> {
  data: T;
  /** bytes on the wire (Resource Timing, else Content-Length), null if unknown */
  bytes: number | null;
  ms: number;
  ttfbMs: number | null;
}

/** GET with a timeout AND an outside AbortSignal (a stale request is
 * cancelled: FieldCancelledError), timed for the connection estimate. */
async function getJsonTimed<T>(url: string, signal: AbortSignal | undefined, ms = FIELD_TIMEOUT_MS): Promise<Timed<T>> {
  const ac = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    ac.abort();
  }, ms);
  const onAbort = () => ac.abort();
  signal?.addEventListener("abort", onAbort);
  const t0 = performance.now();
  try {
    // getJson: the app-wide 401 handling (signed out -> login) still applies
    const data = await getJson<T>(url, { signal: ac.signal });
    const total = performance.now() - t0;
    let bytes: number | null = null;
    let ttfbMs: number | null = null;
    try {
      const abs = new URL(url, window.location.href).href;
      const ent = performance.getEntriesByName(abs).pop() as PerformanceResourceTiming | undefined;
      if (ent) {
        bytes = ent.encodedBodySize || ent.transferSize || null;
        if (ent.responseStart > 0) ttfbMs = ent.responseStart - ent.requestStart;
      }
    } catch {
      /* no Resource Timing */
    }
    return { data, bytes, ms: total, ttfbMs };
  } catch (e) {
    if (timedOut) throw new FieldTimeoutError(ms);
    if (signal?.aborted) throw new FieldCancelledError();
    throw e;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

export type FailureKind = "offline" | "slow" | "server";

/** Why a field request failed: no network at all (fetch's TypeError, or the
 * browser says offline), a timeout (server slow), or a server error. */
export function failureKind(e: unknown): FailureKind {
  if (e instanceof FieldTimeoutError) return "slow";
  if (e instanceof TypeError || !navigator.onLine) return "offline";
  return "server";
}

export const fieldApi = {
  config: () => getJson<FieldConfig>("/api/field/config"),
  nearby: (p: { lat: number; lon: number; heading?: number | null; heading_accuracy?: number | null; accuracy?: number | null; asset_type?: string | null; project_id?: string | null }) =>
    getJsonTimeout<NearbyResult>(`/api/field/nearby?${qs(p)}`),
  area: (lat: number, lon: number, r: number) =>
    getJsonTimeout<AreaResult>(`/api/field/area?${qs({ lat: lat.toFixed(6), lon: lon.toFixed(6), r })}`),
  areaTiles: (z: number, tiles: string[], coarse: string[], around: { lat: number; lon: number; r: number } | null, signal?: AbortSignal) =>
    getJsonTimed<AreaTilesResult>(`/api/field/area-tiles?${qs({
      z, tiles: tiles.join(","), coarse: coarse.length ? coarse.join(",") : undefined,
      around: around ? `${around.lat.toFixed(6)},${around.lon.toFixed(6)},${Math.round(around.r)}` : undefined,
    })}`, signal, 15000),
  road: (lat: number, lon: number, heading: number | null) => getJson<RoadContext>(`/api/field/road?${qs({ lat, lon, heading })}`),
  liveJobs: (lat: number, lon: number, r = 800) => getJson<{ jobs: LiveJob[]; note: string }>(`/api/field/live-jobs?${qs({ lat, lon, r })}`),
  offlinePack: (lat: number, lon: number, r = 250) =>
    getJson<{ center: [number, number]; radius_m: number; n: number; assets: Candidate[]; note: string }>(`/api/field/offline-pack?${qs({ lat, lon, r })}`),
  captures: (p: { mine?: boolean; project_id?: string; status?: string; asset_id?: string } = {}) =>
    getJson<Capture[]>(`/api/field/captures?${qs({ mine: p.mine ? "true" : undefined, project_id: p.project_id, status: p.status, asset_id: p.asset_id })}`),
  capture: (id: number) => getJson<Capture>(`/api/field/captures/${id}`),
  reviewQueue: (status = "submitted") => getJson<Capture[]>(`/api/field/review?${qs({ status })}`),
  review: (id: number, status: string, note?: string) => postJson<Capture>(`/api/field/captures/${id}/review`, { status, note }),
  proposeIssue: (id: number, note?: string) =>
    postJson<{ capture: Capture; issue: { id: number }; existing: boolean }>(`/api/field/captures/${id}/propose-issue`, { note }),
  confirmFailure: (id: number, failure_component: string, note?: string) =>
    postJson<{ capture: Capture; export_rows: number }>(`/api/field/captures/${id}/confirm-failure`, { failure_component, note }),
  blurRegions: (captureId: number, photoId: number) =>
    getJson<BlurRegions>(`/api/field/captures/${captureId}/photos/${photoId}/regions`),
  saveBlurRegions: (captureId: number, photoId: number, manual: BlurRegion[], suppressed: string[]) =>
    putJson<BlurRegions>(`/api/field/captures/${captureId}/photos/${photoId}/regions`, {
      manual: manual.map((m) => ({ box: m.box, kind: m.kind })),
      suppressed,
    }),
  reprocess: (captureId: number) => postJson<{ job_id: string | null }>(`/api/field/captures/${captureId}/reprocess`, {}),
  history: (assetId: string) => getJson<AssetHistory>(`/api/field/assets/${encodeURIComponent(assetId)}/history`),
  projects: () => getJson<FieldProject[]>("/api/field/projects"),
  arAssets: (p: { lat: number; lon: number; accuracy?: number | null; heading_accuracy?: number | null; r?: number }) =>
    getJson<ArAssets>(`/api/field/ar-assets?${qs(p)}`),
  arCalibrate: (body: { lat: number; lon: number; accuracy: number | null; asset_id: string; tap_azimuth_deg: number; tap_ground_range_m: number | null;
    mode: "position" | "full" }) =>
    postJson<ArCalibration>("/api/field/ar-calibrate", body),
  /** 2026-10-06 direct manipulation (hold-and-drag, two-finger twist, two dragged points): the server re-solves and judges */
  arCalibrateDirect: (body: { lat: number; lon: number; accuracy: number | null; mode: "drag" | "rotate" | "two_point";
    pairs: { map_e: number; map_n: number; true_e: number; true_n: number }[]; rotation_deg?: number | null }) =>
    postJson<ArDirectCalibration>("/api/field/ar-calibrate", body),
  arCalCandidates: (body: { lat: number; lon: number; accuracy: number | null; heading_accuracy: number | null; tap_azimuth_deg: number;
    tap_ground_range_m: number | null; kind?: string | null }) =>
    postJson<ArCalCandidates>("/api/field/ar-calibrate/candidates", body),
  arAlignRoad: (body: { lat: number; lon: number; accuracy: number | null; camera_heading_deg: number; road_key?: string | null }) =>
    postJson<ArHeadingFix>("/api/field/ar-align-road", body),
  arAlignKerb: (body: { lat: number; lon: number; accuracy: number | null; p1_e: number; p1_n: number; p2_e: number; p2_n: number;
    road_key?: string | null }) =>
    postJson<ArHeadingFix>("/api/field/ar-align-kerb", body),
  arAutoAlign: (frame: Blob, meta: FrameMeta) => postFrame<ArHeadingFix>("/api/field/ar-auto-align", frame, meta),
  /** camera-assisted "Line up with the road" (2026-10-05) */
  arRoadLines: (frame: Blob, meta: FrameMeta) => postFrame<ArRoadLines>("/api/field/ar-road-lines", frame, meta),
  packManifest: (area: PackArea, zmin: number, zmax: number, layers: string[], name?: string) =>
    getJson<PackManifest>(`/api/field/basemap-packs/manifest?${qs({ ...areaParams(area), zmin, zmax, layers: layers.join(","), name })}`),
  areaAssets: (area: PackArea) =>
    getJson<{ center: [number, number]; bbox: number[]; radius_m: number; n: number; assets: Candidate[] }>(`/api/field/offline-pack/area?${qs(areaParams(area))}`),
  projectSite: (id: string) =>
    getJson<{ project: { id: string; name: string; phase_label: string | null }; pipes: { asset_id: string; path: [number, number][] }[]; captures: Capture[] }>(
      `/api/field/projects/${encodeURIComponent(id)}/site`,
    ),
  /** one asset's geometry, for "Directions" / "Guide me" (2026-10-08) */
  locate: (assetId: string) => getJsonTimeout<LocatedAsset>(`/api/field/locate?${qs({ asset_id: assetId })}`),
};

export interface LocatedAsset {
  asset_id: string;
  asset_type: string;
  kind: "point" | "line";
  label: string;
  network?: string | null;
  diameter_mm?: number | null;
  path?: [number, number][];
  lon?: number;
  lat?: number;
}

export interface FrameMeta {
  lat: number;
  lon: number;
  accuracy: number | null;
  heading: number;
  beta: number;
  gamma: number;
  screen_angle: number;
  fov_deg: number;
  /** "Not this road?": only try this road (a key from `alternatives`) */
  road_key?: string | null;
}

/** One camera frame + its pose, multipart (processed in memory on the server, never stored). */
async function postFrame<T>(url: string, frame: Blob, meta: FrameMeta): Promise<T> {
  const fd = new FormData();
  fd.append("meta", JSON.stringify(meta));
  fd.append("image", frame, "frame.jpg");
  const res = await fetch(url, { method: "POST", body: fd });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { detail?: unknown };
      if (typeof j.detail === "string") msg = j.detail;
    } catch {
      /* not JSON */
    }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function photoUrl(c: Capture, p: CapturePhoto, variant: "thumb" | "shared" | "original" | "unblurred"): string {
  // ?r=<blur revision>: a reviewer's blur edit changes the shared copy
  const rev = p.redaction?.rev;
  const q = rev && (variant === "thumb" || variant === "shared") ? `?r=${rev}` : "";
  return `/api/field/captures/${c.id}/photos/${p.id}/${variant}${q}`;
}
